import React, { VoidFunctionComponent, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordValidator } from 'record-it-utils/password/PasswordValidator/PasswordValidator';
import { ButtonsContainer } from '../../button/ButtonsContainer/ButtonsContainer';
import {
  ButtonStandard,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import { useAsyncCallback } from '../../hooks/useAsyncCallback/useAsyncCallback';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { useOpened } from '../../hooks/useOpened/useOpened';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { PasswordRulesBox } from '../../misc/PasswordRulesBox/PasswordRulesBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import {
  ExpandableInlineCreationForm,
  ExpandableInlineCreationFormProps
} from '../ExpandableInlineCreationForm/ExpandableInlineCreationForm';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './ExpandableChangePasswordForm.module.css';

export type ExpandableChangePasswordFormProps = PropsWithoutChildren<
  Omit<
    ExpandableInlineCreationFormProps,
    'opened' | 'label' | 'openedContent' | 'notOpenedContent' | 'buttons'
  >
> & {
  changePassword: ChangePassword;
};

export type ChangePassword = (options: {
  currentPassword: string;
  newPassword: string;
}) => Promise<void>;

export const ExpandableChangePasswordForm: VoidFunctionComponent<ExpandableChangePasswordFormProps> =
  React.memo((props) => {
    const { changePassword, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const notificationManager = useNotificationManager();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { t } = useTranslation('record-it-components');
    const { open, close, opened } = useOpened();

    const closeAndReset = useEventCallback(() => {
      setCurrentPassword('');
      setNewPassword('');
      setErrorMessage(null);
      close();
    });

    const handleChangeToNewPasswordClick = useEventCallback(() => {
      if (!currentPassword) {
        setErrorMessage(
          t('form.ExpandableChangePasswordForm.currentPasswordIsRequired')
        );
        return;
      }

      startChangePassword({
        currentPassword,
        newPassword
      });
    });

    const { startChangePassword, changePasswordIsRunning } = useAsyncCallback({
      callback: changePassword,
      name: 'changePassword',
      onSuccess: () => {
        notificationManager.success({
          title: t(
            'form.ExpandableChangePasswordForm.changeToNewPasswordSuccess'
          ),
          message: ''
        });

        closeAndReset();
      },
      onError: (error) => {
        console.error(error);
        setErrorMessage(
          t('form.ExpandableChangePasswordForm.changeToNewPasswordError')
        );
      }
    });

    const passwordValidity = useMemo(() => {
      const validator = new PasswordValidator();
      return validator.validate(newPassword);
    }, [newPassword]);

    return (
      <ExpandableInlineCreationForm
        {...remainingProps}
        opened={opened}
        label={t('form.ExpandableChangePasswordForm.label')}
        openedContent={
          <div className={styles.formWrapper}>
            {errorMessage ? (
              <FormGroup>
                <MessageBox
                  type={MessageBoxType.Error}
                  data-testid="ExpandableChangePasswordForm.ErrorMessageBox"
                >
                  {errorMessage}
                </MessageBox>
              </FormGroup>
            ) : null}
            <FormGroup>
              <TextInput
                label={t('form.ExpandableChangePasswordForm.currentPassword')}
                type={TextInputType.Password}
                value={currentPassword}
                onInput={setCurrentPassword}
                data-testid="ExpandableChangePasswordForm.CurrentPasswordInput"
              ></TextInput>
            </FormGroup>
            <FormGroup>
              <TextInput
                label={t('form.ExpandableChangePasswordForm.newPassword')}
                type={TextInputType.Password}
                value={newPassword}
                onInput={setNewPassword}
                data-testid="ExpandableChangePasswordForm.NewPasswordInput"
              ></TextInput>
              <PasswordRulesBox
                passwordValidity={passwordValidity}
                className={styles.passwordRuleBox}
              ></PasswordRulesBox>
            </FormGroup>

            <LoadingOverlay
              active={changePasswordIsRunning}
              data-testid="ExpandableChangePasswordForm.LoadingOverlay"
            ></LoadingOverlay>
          </div>
        }
        buttons={
          <ButtonsContainer>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleChangeToNewPasswordClick}
              disabled={!passwordValidity.valid || changePasswordIsRunning}
              data-testid="ExpandableChangePasswordForm.ChangeToNewPassword"
            >
              {t('form.ExpandableChangePasswordForm.changeToNewPassword')}
            </ButtonStandard>
            <ButtonStandard
              type={ButtonStandardType.Secondary}
              onClick={closeAndReset}
              disabled={changePasswordIsRunning}
              data-testid="ExpandableChangePasswordForm.Cancel"
            >
              {t('form.ExpandableChangePasswordForm.cancel')}
            </ButtonStandard>
          </ButtonsContainer>
        }
        notOpenedContent={
          <ButtonsContainer>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={open}
              data-testid="ExpandableChangePasswordForm.ChangePassword"
            >
              {t('form.ExpandableChangePasswordForm.changePassword')}
            </ButtonStandard>
          </ButtonsContainer>
        }
      ></ExpandableInlineCreationForm>
    );
  });
